import DirectionText from "@/components/DirectionText/DirectionText"
import { useProfileData, useTopOneData } from "@/queries/queries"
import type { Achievement } from "@/types/homeTypes"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FaUserAlt } from "react-icons/fa"

const CARD_WIDTH = 250

export default function TopOne() {
  const topOneQuery = useTopOneData()
  const profileQuery = useProfileData()

  const { t, i18n } = useTranslation()

  const [achievements, setAchievements] = useState<Array<Achievement>>(
    topOneQuery.data?.achievements || []
  )

  /*
    this effect calculates the number of achievements needed to fill the topOne carousel
    then duplicates the achievements returned from the api to create a seamless infinite scroll
    works dynamically with any number of achievements, and any screen width.
  */
  useEffect(() => {
    if (!topOneQuery.data?.achievements?.length) {
      setAchievements([])
      return
    }
    const topOneCarousel = document.querySelector(
      ".topOneCarousel"
    ) as HTMLDivElement
    if (!topOneCarousel) {
      setAchievements(topOneQuery.data.achievements)
      return
    }

    const topOneCarouselWidth = topOneCarousel.offsetWidth
    const countOfCards = topOneQuery.data?.achievements.length || 0
    const widthOfAllCards = countOfCards * CARD_WIDTH

    const newAchievements = [...topOneQuery.data.achievements]
    if (widthOfAllCards < topOneCarouselWidth) {
      const countOfAchievementsToDuplicate = Math.floor(
        (topOneCarouselWidth - widthOfAllCards) / CARD_WIDTH
      )
      const achievementArrayRepeatCount = Math.ceil(
        countOfAchievementsToDuplicate / countOfCards
      )

      for (let i = 0; i < achievementArrayRepeatCount; i++) {
        newAchievements.push(...topOneQuery.data.achievements)
      }

      // repeat the data once more to create a seamless transition after the first loop finishes
    }
    setAchievements([...newAchievements, ...newAchievements])
  }, [topOneQuery.data?.achievements])

  if (!topOneQuery.data) return null

  return (
    <>
      <style>
        {`
        @keyframes scrolling {
          0% {
            transform: translateX(0%);
            }
          100% {
            transform: translateX(calc(-255px * ${topOneQuery.data?.achievements.length || 0} * ${i18n.language === "ar" ? -1 : 1}));
          }
        }

        .topOneCarousel {
          animation: scrolling ${(topOneQuery.data?.achievements.length || 0) * 1.7}s linear infinite;
          will-change: transform;

          &:hover {
            animation-play-state: paused;
          }
        }

        @keyframes gold-glow {
          0% { box-shadow: 0 0 15px rgba(255, 215, 0, 0.4), inset 0 0 5px rgba(255, 215, 0, 0.2); }
          50% { box-shadow: 0 0 30px rgba(255, 215, 0, 0.7), inset 0 0 10px rgba(255, 215, 0, 0.4); }
          100% { box-shadow: 0 0 15px rgba(255, 215, 0, 0.4), inset 0 0 5px rgba(255, 215, 0, 0.2); }
        }

        @keyframes rays-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .is-me-card {
          animation: gold-glow 2s infinite alternate !important;
          border-color: #ffd700 !important;
          background-color: rgba(255, 215, 0, 0.2) !important;
          z-index: 2;
        }

        .rays-glow-bg {
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: -1;
          pointer-events: none;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .rays-glow-bg::before {
          content: '';
          position: absolute;
          width: 450px;
          height: 450px;
          background: conic-gradient(
            from 0deg,
            transparent 0deg,
            rgba(255, 215, 0, 0) 9deg,
            rgba(255, 215, 0, 0.25) 17deg,
            rgba(255, 215, 0, 0.25) 26deg,
            rgba(255, 215, 0, 0) 34deg,
            transparent 51deg,
            rgba(255, 215, 0, 0) 60deg,
            rgba(255, 215, 0, 0.25) 69deg,
            rgba(255, 215, 0, 0.25) 77deg,
            rgba(255, 215, 0, 0) 86deg,
            transparent 103deg,
            rgba(255, 215, 0, 0) 111deg,
            rgba(255, 215, 0, 0.25) 120deg,
            rgba(255, 215, 0, 0.25) 129deg,
            rgba(255, 215, 0, 0) 137deg,
            transparent 154deg,
            rgba(255, 215, 0, 0) 163deg,
            rgba(255, 215, 0, 0.25) 171deg,
            rgba(255, 215, 0, 0.25) 180deg,
            rgba(255, 215, 0, 0) 189deg,
            transparent 206deg,
            rgba(255, 215, 0, 0) 214deg,
            rgba(255, 215, 0, 0.25) 223deg,
            rgba(255, 215, 0, 0.25) 231deg,
            rgba(255, 215, 0, 0) 240deg,
            transparent 257deg,
            rgba(255, 215, 0, 0) 266deg,
            rgba(255, 215, 0, 0.25) 274deg,
            rgba(255, 215, 0, 0.25) 283deg,
            rgba(255, 215, 0, 0) 291deg,
            transparent 309deg,
            rgba(255, 215, 0, 0) 317deg,
            rgba(255, 215, 0, 0.25) 326deg,
            rgba(255, 215, 0, 0.25) 334deg,
            rgba(255, 215, 0, 0) 343deg,
            transparent 360deg
          );
          animation: rays-rotate 20s linear infinite;
          mask-image: radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 70%);
          -webkit-mask-image: radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 70%);
        }

        .rays-glow-bg::after {
          content: '';
          position: absolute;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%);
        }

        `}
      </style>
      <div
        style={{
          paddingBlock: "24px",
          color: "white",
          borderRadius: "20px",
          backgroundImage:
            "linear-gradient(135deg, var(--primary) 0%, #3a5ba0 100%)",
          marginBottom: "0",
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          position: "relative",
        }}
      >
        <div className="px-4 d-flex gap-3 align-items-center mb-4">
          <span style={{ fontSize: "28px", marginTop: "-10px" }}>⭐</span>
          <div>
            <h3 className="m-0 fw-bold">{"EraaSoft Top One"}</h3>
            <p className="m-0 opacity-75 fs-6">
              {t("homePage.topOne.subHeading")} {topOneQuery.data?.course.title}
            </p>
          </div>
        </div>
        <div className={"topOneCarousel d-flex gap-3 px-4 pb-2 w-100"}>
          {achievements.map((achievement, index) => (
            <TopOneCard
              key={`${achievement.id}-${index}`}
              achievement={achievement}
              isMe={achievement.user_id === profileQuery.data?.id}
            />
          ))}
        </div>
      </div>
    </>
  )
}

function TopOneCard({
  achievement,
  isMe,
}: {
  achievement: Achievement
  isMe?: boolean
}) {
  const [isDefaultImage, setIsDefaultImage] = useState<boolean>(false)

  return (
    <div
      key={achievement.id}
      style={{
        width: `${CARD_WIDTH}px`,
        border: isMe
          ? "2px solid #ffd700"
          : "1px solid rgba(255, 255, 255, 0.2)",
        backgroundColor: isMe
          ? "rgba(255, 215, 0, 0.15)"
          : "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: isMe ? "visible" : "hidden",
      }}
      className={`d-flex flex-shrink-0 ${isMe ? "is-me-card" : ""}`}
    >
      <div
        className="w-100  px-3 py-3 position-relative d-flex justify-content-between align-items-center flex-shrink-0"
        style={{
          backgroundColor: isMe ? "#6074a3ff" : "transparent",
          borderRadius: "16px",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            overflow: "hidden",
            border: "2px solid #ffd700",
            boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
            marginInlineEnd: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          }}
          className="d-flex justify-content-center align-items-center"
        >
          {isDefaultImage || !achievement.user.image ? (
            <FaUserAlt size={24} color="rgba(255,255,255,0.8)" />
          ) : (
            <img
              src={achievement.user.image}
              alt="student profile image"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => {
                setIsDefaultImage(true)
              }}
            />
          )}
        </div>
        <div className="flex-grow-1">
          <DirectionText
            style={{
              textWrap: "pretty",
              fontWeight: "600",
              fontSize: "0.95rem",
            }}
          >
            {achievement.user.name}
          </DirectionText>
        </div>
      </div>
      {isMe && <div className="rays-glow-bg" />}
    </div>
  )
}
